import { useState } from 'react';
import { Play, Shuffle, Search, Zap, Target, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Match, MatchMode, Difficulty, MODE_LABELS, DIFFICULTY_LABELS, CLICK_LIMITS } from '@/types/game';
import { getRandomArticle, searchArticles } from '@/lib/wikipedia';
import { AdSlot } from '@/components/ads/AdSlot';

interface MatchSetupProps {
  onStartMatch: (match: Match) => void;
  showAd?: boolean;
}

export function MatchSetup({ onStartMatch, showAd = false }: MatchSetupProps) {
  const [startTitle, setStartTitle] = useState('');
  const [targetTitle, setTargetTitle] = useState('');
  const [mode, setMode] = useState<MatchMode>('time_attack');
  const [difficulty, setDifficulty] = useState<Difficulty>('normal');
  const [isLoadingRandom, setIsLoadingRandom] = useState(false);
  const [startSuggestions, setStartSuggestions] = useState<string[]>([]);
  const [targetSuggestions, setTargetSuggestions] = useState<string[]>([]);
  const [showStartSuggestions, setShowStartSuggestions] = useState(false);
  const [showTargetSuggestions, setShowTargetSuggestions] = useState(false);

  const handleRandomize = async () => {
    setIsLoadingRandom(true);
    try {
      const [randomStart, randomTarget] = await Promise.all([
        getRandomArticle(),
        getRandomArticle(),
      ]);
      setStartTitle(randomStart);
      setTargetTitle(randomTarget);
    } catch (err) {
      console.error('Failed to get random articles:', err);
    } finally {
      setIsLoadingRandom(false);
    }
  };

  const handleSearch = async (query: string, type: 'start' | 'target') => {
    if (query.length < 2) {
      if (type === 'start') setStartSuggestions([]);
      else setTargetSuggestions([]);
      return;
    }
    const results = await searchArticles(query, 5);
    if (type === 'start') {
      setStartSuggestions(results);
      setShowStartSuggestions(true);
    } else {
      setTargetSuggestions(results);
      setShowTargetSuggestions(true);
    }
  };

  const handleStartMatch = () => {
    if (!startTitle || !targetTitle) return;
    const match: Match = {
      id: crypto.randomUUID(),
      lang_code: 'en',
      start_title: startTitle,
      target_title: targetTitle,
      mode,
      difficulty,
      seed: crypto.randomUUID(),
      is_daily: false,
      created_by: null,
      created_at: new Date().toISOString(),
    };
    onStartMatch(match);
  };

  return (
    <div className="max-w-lg mx-auto">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold mb-2">New Game</h1>
        <p className="text-muted-foreground">
          Navigate from one Wikipedia article to another using only links.
        </p>
      </div>

      {showAd && (
        <div className="mb-6">
          <AdSlot placement="pre-game" />
        </div>
      )}

      <div className="game-card space-y-6">
        {/* Article selection */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Articles</Label>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRandomize}
              disabled={isLoadingRandom}
              className="gap-2 text-muted-foreground hover:text-foreground"
            >
              {isLoadingRandom ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Shuffle className="w-4 h-4" />
              )}
              Random
            </Button>
          </div>

          <div className="relative">
            <Label htmlFor="start" className="text-xs text-muted-foreground mb-1.5 block">
              Start
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="start"
                value={startTitle}
                onChange={(e) => {
                  setStartTitle(e.target.value);
                  handleSearch(e.target.value, 'start');
                }}
                onFocus={() => setShowStartSuggestions(startSuggestions.length > 0)}
                onBlur={() => setTimeout(() => setShowStartSuggestions(false), 200)}
                placeholder="e.g., Albert Einstein"
                className="pl-10"
              />
            </div>
            {showStartSuggestions && startSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg overflow-hidden">
                {startSuggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    className="w-full px-4 py-2.5 text-left hover:bg-muted/50 text-sm transition-colors"
                    onClick={() => {
                      setStartTitle(suggestion);
                      setShowStartSuggestions(false);
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <Label htmlFor="target" className="text-xs text-muted-foreground mb-1.5 block">
              Target
            </Label>
            <div className="relative">
              <Target className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="target"
                value={targetTitle}
                onChange={(e) => {
                  setTargetTitle(e.target.value);
                  handleSearch(e.target.value, 'target');
                }}
                onFocus={() => setShowTargetSuggestions(targetSuggestions.length > 0)}
                onBlur={() => setTimeout(() => setShowTargetSuggestions(false), 200)}
                placeholder="e.g., Pizza"
                className="pl-10"
              />
            </div>
            {showTargetSuggestions && targetSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg overflow-hidden">
                {targetSuggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    className="w-full px-4 py-2.5 text-left hover:bg-muted/50 text-sm transition-colors"
                    onClick={() => {
                      setTargetTitle(suggestion);
                      setShowTargetSuggestions(false);
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mode selection */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Mode</Label>
          <RadioGroup
            value={mode}
            onValueChange={(value) => setMode(value as MatchMode)}
            className="grid grid-cols-2 gap-3"
          >
            <label
              className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                mode === 'time_attack'
                  ? 'border-accent bg-accent/5'
                  : 'border-border hover:border-muted-foreground/50'
              }`}
            >
              <RadioGroupItem value="time_attack" id="time_attack" className="sr-only" />
              <Zap className={`w-5 h-5 ${mode === 'time_attack' ? 'text-accent' : 'text-muted-foreground'}`} />
              <div>
                <span className="font-medium text-sm">{MODE_LABELS.time_attack}</span>
                <p className="text-xs text-muted-foreground mt-0.5">Fastest time wins</p>
              </div>
            </label>
            <label
              className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                mode === 'under_clicks'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground/50'
              }`}
            >
              <RadioGroupItem value="under_clicks" id="under_clicks" className="sr-only" />
              <Target className={`w-5 h-5 ${mode === 'under_clicks' ? 'text-primary' : 'text-muted-foreground'}`} />
              <div>
                <span className="font-medium text-sm">{MODE_LABELS.under_clicks}</span>
                <p className="text-xs text-muted-foreground mt-0.5">Stay under limit</p>
              </div>
            </label>
          </RadioGroup>
        </div>

        {/* Difficulty selection */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Difficulty</Label>
          <RadioGroup
            value={difficulty}
            onValueChange={(value) => setDifficulty(value as Difficulty)}
            className="grid grid-cols-4 gap-2"
          >
            {(['easy', 'normal', 'hard', 'insane'] as Difficulty[]).map((d) => (
              <label
                key={d}
                className={`flex flex-col items-center p-3 rounded-lg border cursor-pointer transition-all ${
                  difficulty === d
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/50'
                }`}
              >
                <RadioGroupItem value={d} id={d} className="sr-only" />
                <span className="font-medium text-sm">{DIFFICULTY_LABELS[d]}</span>
                {mode === 'under_clicks' && (
                  <span className="text-xs text-muted-foreground mt-0.5">
                    {CLICK_LIMITS[d]} clicks
                  </span>
                )}
              </label>
            ))}
          </RadioGroup>
        </div>

        {/* Start button */}
        <Button
          onClick={handleStartMatch}
          disabled={!startTitle || !targetTitle}
          className="w-full gap-2"
        >
          <Play className="w-5 h-5" />
          Start Game
        </Button>
      </div>
    </div>
  );
}
